import { CentralIaTypes } from './types';

export const sendChatMessageRequest = () => ({
  type: CentralIaTypes.SEND_CHAT_MESSAGE_REQUEST,
});

export const chatStreamStart = (chat: any, userMessage: any) => ({
  type: CentralIaTypes.CHAT_STREAM_START,
  payload: { chat, userMessage },
});

export const chatStreamChunk = (content: string) => ({
  type: CentralIaTypes.CHAT_STREAM_CHUNK,
  payload: { content },
});

export const chatStreamDone = (chat: any, assistantMessage: any) => ({
  type: CentralIaTypes.CHAT_STREAM_DONE,
  payload: { chat, assistantMessage },
});

export const chatStreamTitle = (chatId: number, title: string) => ({
  type: CentralIaTypes.CHAT_STREAM_TITLE,
  payload: { chatId, title },
});

export const chatStreamError = (message?: string | null, chat?: any) => ({
  type: CentralIaTypes.CHAT_STREAM_ERROR,
  payload: { message, chat },
});

export const chatStreamStopped = () => ({
  type: CentralIaTypes.CHAT_STREAM_STOPPED,
});

export const chatFilesMissing = (fileIds: Array<string | number>) => ({
  type: CentralIaTypes.CHAT_FILES_MISSING,
  payload: { fileIds },
});

export const generateImage = (params: { prompt: string; responseFormat?: string; n?: number }) => ({
  types: [
    CentralIaTypes.GENERATE_IMAGE_REQUEST,
    CentralIaTypes.GENERATE_IMAGE_SUCCESS,
    CentralIaTypes.GENERATE_IMAGE_FAILURE,
  ],

  promise: (client: any) =>
    client.post(
      '/chatbots/central_ia/images',
      {
        prompt: params.prompt,
        response_format: params.responseFormat ?? 'b64_json',
        n: params.n ?? 1,
      },
      { timeout: 130 * 1000 },
    ),
});

export const createFolder = (name: string) => ({
  types: [
    CentralIaTypes.CREATE_FOLDER_REQUEST,
    CentralIaTypes.CREATE_FOLDER_SUCCESS,
    CentralIaTypes.CREATE_FOLDER_FAILURE,
  ],
  folderName: name,

  promise: (client: any) =>
    client.post('/chatbots/central_ia/folders', {
      name,
    }),
});

export const resetCreateFolder = () => ({
  type: CentralIaTypes.RESET_CREATE_FOLDER,
});

export const fetchFolders = () => ({
  types: [
    CentralIaTypes.FETCH_FOLDERS_REQUEST,
    CentralIaTypes.FETCH_FOLDERS_SUCCESS,
    CentralIaTypes.FETCH_FOLDERS_FAILURE,
  ],

  promise: (client: any) => client.get('/chatbots/central_ia/folders'),
});

export const deleteFolder = (id: number | string) => ({
  types: [
    CentralIaTypes.DELETE_FOLDER_REQUEST,
    CentralIaTypes.DELETE_FOLDER_SUCCESS,
    CentralIaTypes.DELETE_FOLDER_FAILURE,
  ],
  folderId: id,

  promise: (client: any) => client.delete(`/chatbots/central_ia/folders/${id}`),
});

export const updateFolder = (id: number | string, name: string) => ({
  types: [
    CentralIaTypes.UPDATE_FOLDER_REQUEST,
    CentralIaTypes.UPDATE_FOLDER_SUCCESS,
    CentralIaTypes.UPDATE_FOLDER_FAILURE,
  ],
  folderId: id,
  folderName: name,

  promise: (client: any) =>
    client.put(`/chatbots/central_ia/folders/${id}`, {
      name,
    }),
});

export const fetchChats = () => ({
  types: [
    CentralIaTypes.FETCH_CHATS_REQUEST,
    CentralIaTypes.FETCH_CHATS_SUCCESS,
    CentralIaTypes.FETCH_CHATS_FAILURE,
  ],

  promise: (client: any) => client.get('/chatbots/central_ia/chats'),
});

export const deleteChat = (id: number | string) => ({
  types: [
    CentralIaTypes.DELETE_CHAT_REQUEST,
    CentralIaTypes.DELETE_CHAT_SUCCESS,
    CentralIaTypes.DELETE_CHAT_FAILURE,
  ],
  chatId: id,

  promise: (client: any) => client.delete(`/chatbots/central_ia/chats/${id}`),
});

export const fetchChat = (id: number | string) => ({
  types: [
    CentralIaTypes.FETCH_CHAT_REQUEST,
    CentralIaTypes.FETCH_CHAT_SUCCESS,
    CentralIaTypes.FETCH_CHAT_FAILURE,
  ],
  chatId: id,

  promise: (client: any) => client.get(`/chatbots/central_ia/chats/${id}`),
});

export const updateChat = (id: number | string, title: string) => ({
  types: [
    CentralIaTypes.UPDATE_CHAT_REQUEST,
    CentralIaTypes.UPDATE_CHAT_SUCCESS,
    CentralIaTypes.UPDATE_CHAT_FAILURE,
  ],
  chatId: id,
  chatTitle: title,

  promise: (client: any) =>
    client.put(`/chatbots/central_ia/chats/${id}`, {
      title,
    }),
});

export const uploadFile = (file: File) => ({
  types: [
    CentralIaTypes.UPLOAD_FILE_REQUEST,
    CentralIaTypes.UPLOAD_FILE_SUCCESS,
    CentralIaTypes.UPLOAD_FILE_FAILURE,
  ],

  promise: (client: any) => {
    const formData = new FormData();
    formData.append('file', file);
    return client.post('/chatbots/central_ia/files', formData);
  },
});

export const removePendingFile = (fileId: string | number) => ({
  type: CentralIaTypes.REMOVE_PENDING_FILE,
  payload: { fileId },
});

export const clearPendingFiles = () => ({
  type: CentralIaTypes.CLEAR_PENDING_FILES,
});

export const removeChatFile = (fileId: string | number) => ({
  type: CentralIaTypes.REMOVE_CHAT_FILE,
  payload: { fileId },
});

export const moveChat = (id: number | string, folderId: number | null) => ({
  types: [
    CentralIaTypes.MOVE_CHAT_REQUEST,
    CentralIaTypes.MOVE_CHAT_SUCCESS,
    CentralIaTypes.MOVE_CHAT_FAILURE,
  ],
  chatId: id,
  targetFolderId: folderId,

  promise: (client: any) =>
    client.put(`/chatbots/central_ia/chats/${id}`, {
      folder_id: folderId,
    }),
});

export const addUserMessage = (content: string) => ({
  type: CentralIaTypes.ADD_USER_MESSAGE,
  payload: { content },
});

export const setModel = (model: string) => ({
  type: CentralIaTypes.SET_MODEL,
  payload: { model },
});

export const setProvider = (provider: string) => ({
  type: CentralIaTypes.SET_PROVIDER,
  payload: { provider },
});

export const startChat = () => ({
  type: CentralIaTypes.START_CHAT,
});

export const newChat = () => ({
  type: CentralIaTypes.NEW_CHAT,
});

export const toggleSidebar = () => ({
  type: CentralIaTypes.TOGGLE_SIDEBAR,
});

export const clearChat = () => ({
  type: CentralIaTypes.CLEAR_CHAT,
});
